import { Box, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useState } from 'react';

import { ContratoFormData } from '../../../schemas/contratoSchema';
import Upload from '@assets/icons/upload.svg';
import { formatFileName } from '@utils/formatFileName';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

interface EditalDataProps {
  isSubmitting?: boolean;
}

const EditalData = ({ isSubmitting }: EditalDataProps) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ContratoFormData>();

  const [dragOver, setDragOver] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const base64EditalValue = watch('base64Edital');
  const hasPreloadedFile = !!base64EditalValue && !fileName;

  const handleFileToBase64 = (
    file: File,
    onChange: (value: string) => void,
  ) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(',')[1];
      onChange(base64Data);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box
      sx={{
        boxShadow: '0px 4px 4px 0px #00000040',
        borderRadius: '12px',
        backgroundColor: '#FFF',
        py: { xs: '24px', md: '40px' },
        px: { xs: '24px', md: '88px' },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          fontSize: { xs: '16px', md: '24px' },
          color: '#004AA0',
          marginBottom: { xs: '16px', md: '40px' },
        }}
      >
        Dados do Edital
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: { md: '88px' },
          rowGap: '40px',
        }}
      >
        <Controller
          name="numeroEdital"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Número do Edital*:
              </Typography>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                placeholder={`000000/${String(new Date().getFullYear()).slice(-2)}`}
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${
                    errors.numeroEdital ? '#FF0000' : '#004AA0'
                  }`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
            </Box>
          )}
        />
        <Controller
          name="valorIS"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <div>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Valor desejado*:
              </Typography>
              <input
                {...field}
                value={
                  field.value
                    ? new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(field.value)
                    : ''
                }
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\D/g, '');
                  const float = Number(rawValue) / 100;
                  field.onChange(float);
                }}
                placeholder="R$ 0,00"
                style={{
                  background: 'transparent',
                  flex: 1,
                  color: isSubmitting ? '#808080' : '#000',
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.valorIS ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  outline: 'none',
                  fontSize: '14px',
                }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  mt: '8px',
                  color: '#808080',
                }}
              >
                O valor informado deve ser ≤ limite aprovado.
              </Typography>
            </div>
          )}
        />
        <Controller
          name="dtInicioVigencia"
          control={control}
          render={({ field }) => (
            <Box>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Data Início Vigência*:
              </Typography>
              <DatePicker
                value={field.value ? dayjs(field.value, 'YYYY-MM-DD') : null}
                onChange={(date: Dayjs | null) =>
                  field.onChange(date ? date.format('YYYY-MM-DD') : '')
                }
                format="DD/MM/YYYY"
                placeholder="dd/mm/aaaa"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.dtInicioVigencia ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: isSubmitting ? '#808080' : '#000',
                }}
              />
            </Box>
          )}
        />
        <Controller
          name="dtFimVigencia"
          control={control}
          render={({ field }) => (
            <Box>
              <Typography
                sx={{
                  color: '#004AA0',
                  fontSize: { xs: '14px' },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Data Fim Vigência*:
              </Typography>
              <DatePicker
                value={field.value ? dayjs(field.value, 'YYYY-MM-DD') : null}
                onChange={(date: Dayjs | null) =>
                  field.onChange(date ? date.format('YYYY-MM-DD') : '')
                }
                format="DD/MM/YYYY"
                placeholder="dd/mm/aaaa"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '13px 12px',
                  border: `1px solid ${errors.dtFimVigencia ? '#FF0000' : '#004AA0'}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: isSubmitting ? '#808080' : '#000',
                }}
              />
            </Box>
          )}
        />
        <Controller
          name="base64Edital"
          control={control}
          disabled={isSubmitting}
          render={({ field }) => (
            <Box>
              <Box
                onDragOver={(e) => {
                  if (isSubmitting) return;
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  if (isSubmitting) return;
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file && /(pdf|doc|docx)$/i.test(file.name)) {
                    handleFileToBase64(file, field.onChange);
                  }
                }}
                sx={{
                  opacity: isSubmitting ? 0.6 : 1,
                  pointerEvents: isSubmitting ? 'none' : 'auto',
                  border: `1px solid ${
                    errors.base64Edital ? '#FF0000' : '#004AA0'
                  }`,
                  borderRadius: '4px',
                  height: '169px',
                  padding: '20px',
                  textAlign: 'center',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  background: dragOver ? '#209DDB26' : '#FFF',
                  ':hover': { borderColor: '#004AA0' },
                  transition: 'border-color 0.3s, background-color 0.3s',
                  position: 'relative',
                }}
                onClick={() =>
                  !isSubmitting &&
                  document.getElementById('file-input-edital')?.click()
                }
              >
                {fileName ? (
                  /* Arquivo recém selecionado */
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '20px',
                      padding: { xs: '44px' },
                    }}
                  >
                    <Box
                      component="img"
                      src={Upload}
                      alt="Upload Icon"
                      sx={{
                        width: { xs: '36px', md: '46px' },
                        height: { xs: '44px', md: '56px' },
                      }}
                    />
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '14px',
                        color: '#004AA0',
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {formatFileName(fileName)}
                    </Typography>
                  </Box>
                ) : hasPreloadedFile ? (
                  /* Arquivo carregado da submissão anterior */
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '20px',
                      padding: { xs: '44px' },
                    }}
                  >
                    <Box
                      component="img"
                      src={Upload}
                      alt="Upload Icon"
                      sx={{
                        width: { xs: '36px', md: '46px' },
                        height: { xs: '44px', md: '56px' },
                        opacity: 0.6,
                      }}
                    />
                    <Box sx={{ textAlign: 'left', maxWidth: '220px' }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 700,
                          color: '#004AA0',
                        }}
                      >
                        Edital mantido
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#808080' }}>
                        Clique para substituir o arquivo
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  /* Nenhum arquivo */
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '20px',
                      padding: { xs: '44px' },
                    }}
                  >
                    <Box
                      component="img"
                      src={Upload}
                      alt="Upload Icon"
                      sx={{
                        width: { xs: '36px', md: '46px' },
                        height: { xs: '44px', md: '56px' },
                      }}
                    />
                    <Box sx={{ textAlign: 'left', maxWidth: '220px' }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 700,
                          color: '#209DDB',
                        }}
                      >
                        Upload do edital
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#209DDB',
                        }}
                      >
                        Clique aqui ou arraste o arquivo Formatos aceitos: PDF,
                        DOC, DOCX
                      </Typography>
                    </Box>
                  </Box>
                )}

                <input
                  id="file-input-edital"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileToBase64(file, field.onChange);
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default EditalData;
